import type { ListScheduledMeetingsParams } from './listScheduledMeetings';
import { MeetingResponse } from './types';

export const MESSENGER_ROOM_IDS_MAX = 50;

/** Synthetic Matrix-only hub rows: no backend record (`buildMatrixOnlyMeeting`). */
export const isMatrixOnlyListRow = (meeting: MeetingResponse): boolean =>
  meeting.scheduled_at == null && meeting.id === meeting.matrix_room_id;

/** True when the row represents a backend scheduled meeting (hub or messenger). */
export const isScheduledBackendMeeting = (meeting: MeetingResponse): boolean => meeting.scheduled_at != null;

export const chunkRoomIds = (roomIds: string[]): string[][] => {
  const chunks: string[][] = [];
  for (let i = 0; i < roomIds.length; i += MESSENGER_ROOM_IDS_MAX) {
    chunks.push(roomIds.slice(i, i + MESSENGER_ROOM_IDS_MAX));
  }
  return chunks;
};

// Builds the backend query params from typed client params. Enforces the
// single-primary-filter rule (AC-B2) and emits a key-sorted object so the
// dedupe key in `listScheduledMeetings` is stable regardless of input order.
export const buildListQueryParams = (params?: ListScheduledMeetingsParams): Record<string, string> => {
  const queryParams: Record<string, string> = {};

  const trimmedRoomIds = params?.messengerRoomIds?.map((id) => id.trim()).filter((id) => id.length > 0);

  // Single primary filter: messenger_room_ids > messenger_room_id > workspace_matrix_room_id.
  if (trimmedRoomIds && trimmedRoomIds.length > 0) {
    queryParams['messenger_room_ids'] = [...trimmedRoomIds].sort().join(',');
  } else if (params?.messengerRoomId) {
    queryParams['messenger_room_id'] = params.messengerRoomId;
  } else if (params?.workspaceMatrixRoomId) {
    queryParams['workspace_matrix_room_id'] = params.workspaceMatrixRoomId;
  }

  if (params?.messengerLinkedOnly === true) queryParams['messenger_linked_only'] = 'true';
  if (params?.status) queryParams['status'] = params.status;
  if (params?.type) queryParams['type'] = params.type;

  return Object.fromEntries(
    Object.keys(queryParams)
      .sort()
      .map((key) => [key, queryParams[key]])
  );
};

// Per-room refetch merge: drop the room's stale rows, append fresh rows for
// that room. Rows with `messenger_room_id === null` are never removed.
export const mergeMeetingsForMessengerRoom = (
  existing: MeetingResponse[],
  roomId: string,
  freshForRoom: MeetingResponse[]
): MeetingResponse[] => {
  const rest = existing.filter((meeting) => meeting.messenger_room_id !== roomId);
  return [...rest, ...freshForRoom];
};

/** Keep fresher per-room rows when a bulk payload omits or regresses them (BUG-003 flash). */
export const preserveRoomMeetingsInBulk = (
  prevMeetings: MeetingResponse[],
  bulkMeetings: MeetingResponse[],
  roomIdsToPreserve: string[],
  forcePreserveRoomIds: string[] = []
): MeetingResponse[] => {
  const uniqueRoomIds = [...new Set(roomIdsToPreserve)];
  const forcePreserve = new Set(forcePreserveRoomIds);
  let merged = bulkMeetings;

  for (const roomId of uniqueRoomIds) {
    const prevSlice = prevMeetings.filter((meeting) => meeting.messenger_room_id === roomId);
    if (prevSlice.length === 0) continue;

    const bulkSlice = bulkMeetings.filter((meeting) => meeting.messenger_room_id === roomId);
    const shouldForce = forcePreserve.has(roomId);
    if (!shouldForce && bulkSlice.length >= prevSlice.length) continue;

    merged = mergeMeetingsForMessengerRoom(merged, roomId, prevSlice);
  }

  return merged;
};
